import { cva, type VariantProps } from 'class-variance-authority'
import { UploadCloud } from 'lucide-react'
import { useState, type ComponentPropsWithRef, type ReactNode } from 'react'

import { Button } from '../Button/Button'
import s from './FileDropzone.module.css'

/**
 * Champ de fichier natif qui **se vide après chaque choix** : sinon, rechoisir le même fichier
 * n'émet pas `change` et le geste se perd sans un mot.
 */
/**
 * Un fichier par geste, ou plusieurs d'un coup (`multiple`) — l'import PDF par lot. Deux rappels
 * distincts plutôt qu'un tableau partout : un appelant à fichier unique n'a pas à trier un tableau
 * qui n'en contient jamais qu'un.
 */
export type FileChoice =
  | { multiple?: false; onFile: (file: File) => void; onFiles?: never }
  | { multiple: true; onFiles: (files: File[]) => void; onFile?: never }

export type FileInputProps = Omit<
  ComponentPropsWithRef<'input'>,
  'type' | 'value' | 'defaultValue' | 'onChange' | 'multiple'
> &
  FileChoice

export function FileInput({ multiple, onFile, onFiles, ...props }: FileInputProps) {
  return (
    <input
      {...props}
      type="file"
      multiple={multiple}
      onChange={(event) => {
        const files = [...(event.target.files ?? [])]
        event.target.value = ''
        if (files.length === 0) return
        if (onFiles !== undefined) onFiles(files)
        else if (files[0] !== undefined) onFile?.(files[0])
      }}
    />
  )
}

const dropzone = cva(s.root, {
  variants: {
    orientation: {
      vertical: s.vertical,
      horizontal: s.horizontal,
    },
  },
  defaultVariants: { orientation: 'vertical' },
})

/**
 * ⚠️ `FileInput` couvre la zone, transparent, et reçoit nativement le clic, le dépôt et le
 * clavier : masqué (`display: none`), il ne recevrait plus rien.
 */
export type FileDropzoneProps = VariantProps<typeof dropzone> &
  FileChoice & {
    accept: string
    'aria-label': string
    title: ReactNode
    hint?: ReactNode
    /** Pour l'œil seulement : c'est le champ posé dessus qui reçoit le clic. */
    buttonLabel?: ReactNode
    disabled?: boolean
    className?: string
  }

export function FileDropzone({
  orientation,
  accept,
  'aria-label': label,
  title,
  hint,
  buttonLabel,
  disabled,
  className,
  ...choice
}: FileDropzoneProps) {
  const [dragging, setDragging] = useState(false)

  return (
    <label
      className={dropzone({ orientation, className })}
      data-dragging={dragging || undefined}
      data-disabled={disabled === true || undefined}
    >
      <span className={s.icon}>
        <UploadCloud size={32} aria-hidden />
      </span>
      <span className={s.text}>
        <span className={s.title}>{title}</span>
        {hint !== undefined && <span className={s.hint}>{hint}</span>}
      </span>
      {buttonLabel !== undefined && (
        <Button size="sm" tabIndex={-1} aria-hidden>
          {buttonLabel}
        </Button>
      )}
      <FileInput
        className={s.input}
        accept={accept}
        aria-label={label}
        disabled={disabled}
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
        onDrop={() => setDragging(false)}
        {...choice}
      />
    </label>
  )
}
